<script setup lang="ts">
import { computed } from 'vue'
import { Loader2 } from '@lucide/vue'
import type { ButtonSize, ButtonType } from '../../types'
import { useFormControl } from '../../composables/useFormControl'

const props = withDefaults(
  defineProps<{
    type?: ButtonType
    size?: ButtonSize
    loading?: boolean
    disabled?: boolean
    danger?: boolean
    ghost?: boolean
    block?: boolean
    htmlType?: 'button' | 'submit' | 'reset'
  }>(),
  {
    type: 'default',
    size: undefined,
    loading: false,
    disabled: undefined,
    danger: false,
    ghost: false,
    block: false,
    htmlType: 'button'
  }
)

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

const { disabled, size } = useFormControl(props)
const isDisabled = computed(() => disabled.value || props.loading)
const resolvedHtmlType = computed(() => props.htmlType)
const resolvedType = computed(() => props.type)

function handleClick(event: MouseEvent) {
  if (isDisabled.value) {
    event.preventDefault()
    return
  }
  emit('click', event)
}
</script>

<template>
  <button
    :type="resolvedHtmlType"
    class="rr-btn"
    data-wave="true"
    :class="[
      `rr-btn--${resolvedType}`,
      `rr-btn--${size}`,
      {
        'is-loading': loading,
        'is-disabled': isDisabled,
        'is-danger': danger,
        'is-ghost': ghost,
        'is-block': block
      }
    ]"
    :disabled="isDisabled"
    @click="handleClick"
  >
    <Loader2 v-if="loading" class="rr-btn__loading-icon" :size="size === 'small' ? 12 : 14" aria-hidden="true" />
    <slot name="icon" />
    <span v-if="$slots.default" class="rr-btn__content"><slot /></span>
  </button>
</template>

<style scoped>
.rr-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  box-sizing: border-box;
  margin: 0;
  border: 1px solid transparent;
  border-radius: var(--rr-radius-control, 6px);
  font-family: inherit;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  text-align: center;
  cursor: pointer;
  user-select: none;
  touch-action: manipulation;
  transition: all var(--rr-motion-duration-mid, 200ms) var(--rr-motion-ease-out, ease);
}

.rr-btn__content {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  font-size: inherit;
  line-height: inherit;
}

.rr-btn:focus-visible {
  outline: 2px solid var(--rr-color-primary-border, #c6b9f3);
  outline-offset: 2px;
}

/* Sizes */
.rr-btn--small {
  height: 24px !important;
  min-height: 24px !important;
  max-height: 24px !important;
  padding: 0 7px !important;
  font-size: var(--rr-font-size-xs, 11px);
}

.rr-btn--middle {
  height: 32px !important;
  min-height: 32px !important;
  max-height: 32px !important;
  padding: 0 14px !important;
  font-size: 13px;
}

.rr-btn--large {
  height: 40px !important;
  min-height: 40px !important;
  max-height: 40px !important;
  padding: 0 18px !important;
  font-size: 15px;
}

.rr-btn.is-block {
  display: flex;
  width: 100%;
}

/* Types */
/* Primary */
.rr-btn--primary {
  border-color: var(--rr-color-primary);
  background: var(--rr-color-primary);
  color: var(--rr-color-surface) !important;
}
.rr-btn--primary .rr-btn__content {
  color: var(--rr-color-surface) !important;
}
.rr-btn--primary:hover:not(:disabled) {
  border-color: var(--rr-color-primary-hover);
  background: var(--rr-color-primary-hover);
}
.rr-btn--primary.is-danger {
  border-color: var(--rr-color-danger);
  background: var(--rr-color-danger);
}
.rr-btn--primary.is-danger:hover:not(:disabled) {
  border-color: #9d252c;
  background: #9d252c;
}

/* Secondary / Default */
.rr-btn--default {
  border-color: var(--rr-color-border-strong);
  background: var(--rr-color-surface);
  color: var(--rr-color-text);
}
.rr-btn--default:hover:not(:disabled) {
  border-color: var(--rr-color-primary-border);
  color: var(--rr-color-primary);
  background: var(--rr-color-surface-subtle);
}
.rr-btn--default.is-danger {
  border-color: var(--rr-color-danger-border);
  color: var(--rr-color-danger);
}
.rr-btn--default.is-danger:hover:not(:disabled) {
  border-color: var(--rr-color-danger);
  background: var(--rr-color-danger-soft);
}

/* Ghost */
.rr-btn.is-ghost {
  border-color: var(--rr-color-border);
  background: transparent;
  color: var(--rr-color-text);
}
.rr-btn.is-ghost:hover:not(:disabled) {
  border-color: var(--rr-color-primary-border);
  color: var(--rr-color-primary);
  background: var(--rr-color-primary-soft);
}

/* Link */
.rr-btn--link {
  border-color: transparent;
  background: transparent;
  color: var(--rr-color-primary);
  padding: 0 4px;
}
.rr-btn--link:hover:not(:disabled) {
  text-decoration: underline;
  color: var(--rr-color-primary-hover);
}
.rr-btn--link.is-danger {
  color: var(--rr-color-danger);
}
.rr-btn--link.is-danger:hover:not(:disabled) {
  color: #9d252c;
}

/* Text */
.rr-btn--text {
  border-color: transparent;
  background: transparent;
  color: var(--rr-color-text);
  padding: 0 6px;
}
.rr-btn--text:hover:not(:disabled) {
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-strong);
}

/* Disabled (exclusive of loading) */
.rr-btn:disabled:not(.is-loading),
.rr-btn.is-disabled:not(.is-loading) {
  cursor: not-allowed;
  opacity: 0.55;
  border-color: var(--rr-color-border);
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-subtle) !important;
}

/* Loading */
.rr-btn.is-loading {
  cursor: default;
  pointer-events: none;
  opacity: 0.8;
}

.rr-btn--primary.is-loading {
  border-color: var(--rr-color-primary) !important;
  background: var(--rr-color-primary) !important;
  color: var(--rr-color-surface) !important;
}

.rr-btn--primary.is-danger.is-loading {
  border-color: var(--rr-color-danger) !important;
  background: var(--rr-color-danger) !important;
  color: var(--rr-color-surface) !important;
}

.rr-btn--secondary.is-loading,
.rr-btn--default.is-loading {
  border-color: var(--rr-color-border-strong);
  background: var(--rr-color-surface);
  color: var(--rr-color-text);
}

.rr-btn__loading-icon {
  animation: rr-spin 800ms linear infinite;
}

@keyframes rr-spin {
  to { transform: rotate(360deg); }
}
</style>
